import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

type Buckets = { m1: number | string | null; m5: number | string | null; m15: number | string | null };

type HotArchive = {
  id: number;
  domain: string;
  archive_status: string;
  requests_per_min: number;
  pending_total: number | string;
  due_now: number | string | null;
  scheduled_future: number | string | null;
};

type ErrorRow = {
  kind: string;
  status_code: number | null;
  count: number | string;
};

type DegradedArchive = {
  id: number;
  domain: string;
  degraded_backoff_sec: number;
  consecutive_errors: number;
  last_error: string | null;
};

type WorkerStatus = 'green' | 'yellow' | 'red';

type Worker = {
  worker_instance: string;
  pid: number;
  hostname: string;
  last_seen_at: string;
  age_sec: number;
  last_action: string;
  current_archive_id: number | null;
  current_url: string | null;
  status: WorkerStatus;
};

type Capacity = {
  active_archives: number;
  total_rpm: number;
  actual_rpm_5min: number;
  utilization_pct: number;
  workers_alive: number;
  workers_idle: number;
  verdict: 'quiescent' | 'no_workers' | 'saturated' | 'underutilized' | 'healthy';
  headline: string;
};

type Diagnostics = {
  snapshots: Buckets;
  errors: Buckets;
  queue_by_status: Record<string, number | string>;
  pending_split: {
    due_now_active: number | string | null;
    due_now_frozen: number | string | null;
    scheduled_future_active: number | string | null;
    scheduled_future_frozen: number | string | null;
  };
  hot_archives: HotArchive[];
  in_flight_stuck: number;
  error_breakdown: ErrorRow[];
  degraded_archives: DegradedArchive[];
  workers: Worker[];
  capacity: Capacity;
  generated_at: string;
};

function num(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  return typeof v === 'number' ? v : parseInt(v, 10) || 0;
}

function fmtSec(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

const REFRESH_MS = 15000;

export default function Diagnostics() {
  const [data, setData] = useState<Diagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Chain the next request only after the previous one settles. setInterval
    // would fire on a fixed clock and pile requests up when the backend is
    // slower than the interval — that pile-up wedged php-fpm in 2026-05-13.
    async function load() {
      try {
        const d = await api<Diagnostics>('/diagnostics');
        if (cancelled) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load diagnostics');
      } finally {
        if (!cancelled && !paused) {
          timer.current = window.setTimeout(load, REFRESH_MS);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [paused]);

  if (error) return <div className="card"><h2>Diagnostics</h2><div className="error">{error}</div></div>;
  if (!data) return <div className="card"><h2>Diagnostics</h2><p className="muted">Loading…</p></div>;

  const snap = data.snapshots;
  const errs = data.errors;
  const split = data.pending_split;
  // Bounce-storm signal: many recent errors but few snapshots → worker is
  // burning cycles without making progress. Or pending_future ≫ pending_now
  // and growing.
  const m1Snap = num(snap.m1);
  const m1Err = num(errs.m1);
  // Frozen rows belong to paused/terminated archives — the worker isn't
  // touching them, so they can't cause a bounce storm. Only the active
  // future bucket signals worker churn.
  const ratioWarning = m1Snap === 0 && m1Err === 0 && num(split.scheduled_future_active) > 100;

  return (
    <>
      <div className="card">
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Diagnostics</h2>
          <div className="muted" style={{ fontSize: 12 }}>
            updated {new Date(data.generated_at).toLocaleTimeString()}
            {' · '}
            <button className="secondary" onClick={() => setPaused((p) => !p)}>
              {paused ? 'Resume auto-refresh' : 'Pause auto-refresh'}
            </button>
          </div>
        </div>
        {ratioWarning && (
          <div className="error" style={{ marginTop: 8 }}>
            No snapshots and no errors in the last minute, but {num(split.scheduled_future_active).toLocaleString()} pending
            rows scheduled for the future on active archives. Likely rate-limit bounce storm — see "Hot archives" below.
          </div>
        )}
      </div>

      <CapacityCard cap={data.capacity} />

      <div className="card">
        <h3>Worker activity</h3>
        <table>
          <thead>
            <tr><th></th><th>1 min</th><th>5 min</th><th>15 min</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Snapshots</td>
              <td>{num(snap.m1).toLocaleString()}</td>
              <td>{num(snap.m5).toLocaleString()}</td>
              <td>{num(snap.m15).toLocaleString()}</td>
            </tr>
            <tr>
              <td>Errors</td>
              <td>{num(errs.m1).toLocaleString()}</td>
              <td>{num(errs.m5).toLocaleString()}</td>
              <td>{num(errs.m15).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Queue</h3>
        <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
          {(['pending', 'in_flight', 'done', 'failed'] as const).map((s) => (
            <div key={s}>
              <div className="muted" style={{ fontSize: 12 }}>{s}</div>
              <div style={{ fontSize: 22 }}>{num(data.queue_by_status[s]).toLocaleString()}</div>
            </div>
          ))}
          <PendingTile
            label="pending due now"
            active={num(split.due_now_active)}
            frozen={num(split.due_now_frozen)}
          />
          <PendingTile
            label="pending scheduled-future"
            active={num(split.scheduled_future_active)}
            frozen={num(split.scheduled_future_frozen)}
          />
          <div>
            <div className="muted" style={{ fontSize: 12 }}>in-flight &gt; 60s</div>
            <div style={{ fontSize: 22, color: data.in_flight_stuck > 0 ? '#b00' : undefined }}>
              {data.in_flight_stuck.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Workers</h3>
        {data.workers.length === 0
          ? <p className="muted">No worker has reported a heartbeat in the last hour.</p>
          : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {data.workers.map((w) => <WorkerChip key={w.worker_instance} w={w} />)}
            </div>
          )}
      </div>

      <div className="card">
        <h3>Hot archives</h3>
        {data.hot_archives.length === 0
          ? <p className="muted">No archive has more than 50 pending rows.</p>
          : (
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>RPM</th>
                  <th>Pending</th>
                  <th>Due now</th>
                  <th>Scheduled future</th>
                  <th>Drain time</th>
                </tr>
              </thead>
              <tbody>
                {data.hot_archives.map((a) => {
                  const total = num(a.pending_total);
                  const dueNow = num(a.due_now);
                  // The worker only picks from non-frozen archives — for
                  // paused/terminated rows, "due now > RPM" can't drive a
                  // bounce storm and "drain time" is meaningless because
                  // nothing is being drained. Suppress those signals so the
                  // table doesn't read as "everything is on fire" when the
                  // user has paused a bunch of archives.
                  const isFrozen = a.archive_status === 'paused' || a.archive_status === 'terminated';
                  const overBudget = !isFrozen && dueNow > a.requests_per_min;
                  const drainMin = !isFrozen && a.requests_per_min > 0
                    ? Math.ceil(total / a.requests_per_min)
                    : null;
                  return (
                    <tr key={a.id} style={isFrozen ? { opacity: 0.6 } : undefined}>
                      <td>{a.domain}</td>
                      <td><span className={`status-pill ${a.archive_status}`}>{a.archive_status}</span></td>
                      <td>{a.requests_per_min}</td>
                      <td>{total.toLocaleString()}</td>
                      <td style={{ color: overBudget ? '#b00' : undefined }}>
                        {dueNow.toLocaleString()}
                        {overBudget && <span title="Due-now exceeds the per-minute rate budget — likely bouncing"> ⚠</span>}
                      </td>
                      <td>{num(a.scheduled_future).toLocaleString()}</td>
                      <td className="muted">
                        {isFrozen ? '—' : drainMin !== null ? `~${drainMin.toLocaleString()} min` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      {data.degraded_archives.length > 0 && (
        <div className="card">
          <h3>Degraded archives</h3>
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Backoff</th>
                <th>Consecutive errors</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {data.degraded_archives.map((a) => (
                <tr key={a.id}>
                  <td><a href={`/archives/${a.id}`}>{a.domain}</a></td>
                  <td>{fmtSec(a.degraded_backoff_sec)}</td>
                  <td>{a.consecutive_errors}</td>
                  <td className="muted" style={{ wordBreak: 'break-word' }}>{a.last_error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3>Errors (last hour)</h3>
        {data.error_breakdown.length === 0
          ? <p className="muted">No errors in the last hour.</p>
          : (
            <table>
              <thead>
                <tr><th>Kind</th><th>Status</th><th>Count</th></tr>
              </thead>
              <tbody>
                {data.error_breakdown.map((e, i) => (
                  <tr key={i}>
                    <td>{e.kind}</td>
                    <td>{e.status_code ?? '—'}</td>
                    <td>{num(e.count).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </>
  );
}

function WorkerChip({ w }: { w: Worker }) {
  // Distinct background+border per status keeps the chip readable even
  // without color (also color-blind friendlier than dot-only).
  const palette: Record<WorkerStatus, { bg: string; border: string; fg: string }> = {
    green: { bg: '#e8f7ec', border: '#7bc492', fg: '#1f6b3a' },
    yellow: { bg: '#fff8e1', border: '#d4a939', fg: '#7a5400' },
    red: { bg: '#fde6e6', border: '#d97070', fg: '#8a1f1f' },
  };
  const c = palette[w.status];
  const ageLabel = w.age_sec < 60
    ? `${w.age_sec}s ago`
    : `${Math.floor(w.age_sec / 60)}m ${w.age_sec % 60}s ago`;
  // Tooltip carries the URL that was being fetched at the last heartbeat —
  // when a worker hangs, this is the diagnostic that names the bad URL.
  const tooltip = [
    `worker @${w.worker_instance} (pid ${w.pid} @ ${w.hostname})`,
    `last seen: ${new Date(w.last_seen_at).toLocaleString()} (${ageLabel})`,
    `action: ${w.last_action}`,
    w.current_url ? `url: ${w.current_url}` : null,
  ].filter(Boolean).join('\n');

  return (
    <div
      title={tooltip}
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.fg,
        borderRadius: 6,
        padding: '8px 12px',
        minWidth: 180,
        fontSize: 13,
        lineHeight: 1.35,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          display: 'inline-block', width: 8, height: 8, borderRadius: 999,
          background: c.border,
        }} />
        <strong>worker @{w.worker_instance}</strong>
        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.8 }}>{ageLabel}</span>
      </div>
      <div style={{ fontSize: 12, marginTop: 2 }}>{w.last_action}</div>
      {w.current_url && (
        <div
          style={{
            fontSize: 11, marginTop: 2, opacity: 0.75,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: 240,
          }}
        >
          {w.current_url}
        </div>
      )}
    </div>
  );
}

/** "Are workers fed?" rolled into one card.
 *
 *  Headline reads off the backend's verdict — quiescent / no_workers /
 *  saturated / underutilized / healthy — each with its own border tone
 *  so the operator can grok the answer without parsing the numbers.
 *  The numbers below (active archives × RPM ceiling vs. actual fetch
 *  rate vs. live/idle workers) are the supporting evidence. */
function CapacityCard({ cap }: { cap: Capacity }) {
  const toneByVerdict: Record<Capacity['verdict'], { border: string; fg: string }> = {
    quiescent: { border: 'var(--border)', fg: 'var(--fg-muted)' },
    no_workers: { border: 'var(--pill-border-stalled)', fg: 'var(--pill-fg-stalled)' },
    saturated: { border: 'var(--pill-border-indexed)', fg: 'var(--pill-fg-indexed)' },
    underutilized: { border: 'var(--border-pending-pill)', fg: 'var(--border-pending-pill-text)' },
    healthy: { border: 'var(--pill-border-indexed)', fg: 'var(--pill-fg-indexed)' },
  };
  const tone = toneByVerdict[cap.verdict];

  return (
    <div className="card" style={{ borderLeft: `4px solid ${tone.border}` }}>
      <h3 style={{ marginTop: 0 }}>Capacity</h3>
      <p style={{ fontSize: 14, marginTop: -6, color: tone.fg, fontWeight: 500 }}>
        {cap.headline}
      </p>
      <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
        <CapacityStat label="Active archives" value={cap.active_archives.toLocaleString()} />
        <CapacityStat
          label="Total RPM cap"
          value={cap.total_rpm.toLocaleString()}
          hint="Sum of requests_per_min across active archives"
        />
        <CapacityStat
          label="Actual RPM"
          value={cap.actual_rpm_5min.toLocaleString()}
          hint="Snapshots in the last 5 min ÷ 5"
        />
        <CapacityStat
          label="Utilization"
          value={`${cap.utilization_pct}%`}
          hint="Actual ÷ ceiling"
        />
        <CapacityStat
          label="Workers idle"
          value={`${cap.workers_idle} of ${cap.workers_alive}`}
          hint="Idle right now / live (green) heartbeats"
        />
      </div>
    </div>
  );
}

function CapacityStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22 }}>{value}</div>
    </div>
  );
}

// The big number is the worker-actionable count; the smaller "frozen" line
// surfaces how much is locked up in paused/terminated archives so the
// number isn't silently inflated like it used to be.
function PendingTile({ label, active, frozen }: { label: string; active: number; frozen: number }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22 }}>{active.toLocaleString()}</div>
      {frozen > 0 && (
        <div className="muted" style={{ fontSize: 11 }}>
          +{frozen.toLocaleString()} frozen in paused
        </div>
      )}
    </div>
  );
}
